import React, { useEffect, useMemo, useRef, useState } from 'react';
import { savePrefab, checkPrefabExists } from '../lib/media';

type Props = {
  open: boolean;
  onClose: () => void;
  componentUrl: string;
  initialProps?: Partial<{ text: string; fontSize: number; textColor: string; bgColor: string; radius: number; padding: number }>;
  prefabMode?: boolean;
};

const ComponentSettingsModal: React.FC<Props> = ({ open, onClose, componentUrl, initialProps, prefabMode }) => {
  const [text, setText] = useState('Get Started');
  const [fontSize, setFontSize] = useState(16);
  const [textColor, setTextColor] = useState('#ffffff');
  const [bgColor, setBgColor] = useState('#111827');
  const [radius, setRadius] = useState(12);
  const [padding, setPadding] = useState(12);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [maximized, setMaximized] = useState(true);
  const [saveOpen, setSaveOpen] = useState(false);
  const [fileName, setFileName] = useState('component-prefab');
  const [displayName, setDisplayName] = useState('Component Prefab');
  const [checkingName, setCheckingName] = useState(false);
  const [nameExists, setNameExists] = useState<boolean | null>(null);
  const [savingPrefab, setSavingPrefab] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // When opening with initialProps (e.g., from a prefab), apply them to local state once
  useEffect(() => {
    if (!open) return;
    if (!initialProps) return;
    if (typeof initialProps.text === 'string') setText(initialProps.text);
    if (typeof initialProps.fontSize === 'number') setFontSize(initialProps.fontSize);
    if (typeof initialProps.textColor === 'string') setTextColor(initialProps.textColor);
    if (typeof initialProps.bgColor === 'string') setBgColor(initialProps.bgColor);
    if (typeof initialProps.radius === 'number') setRadius(initialProps.radius);
    if (typeof initialProps.padding === 'number') setPadding(initialProps.padding);
  }, [open, initialProps]);

  useEffect(() => {
    if (!open) return;
    const send = () => {
      const win = iframeRef.current?.contentWindow;
      if (!win) return;
      win.postMessage({ type: 'SET_PROPS', props: { text, fontSize, textColor, bgColor, radius, padding } }, '*');
    };
    const id = setInterval(send, 200); // nudge after load
    const t = setTimeout(send, 300);
    return () => { clearInterval(id); clearTimeout(t); };
  }, [open, text, fontSize, textColor, bgColor, radius, padding]);

  const src = componentUrl || '';
  const doc = useMemo(() => `<!doctype html><html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/><style>html,body{height:100%;margin:0;padding:0;background:#fff;font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,Ubuntu,Cantarell,Noto Sans,sans-serif}#app{display:flex;align-items:center;justify-content:center;min-height:100%;padding:12px}</style></head><body><div id="app"></div><script>window.__COMPONENT_TARGET__=document.getElementById('app');window.addEventListener('message',function(e){if(e && e.data && e.data.type==='SET_PROPS'){try{if(window.__COMPONENT_SET_PROPS) window.__COMPONENT_SET_PROPS(e.data.props);}catch(err){}}});</script><script src="${src.replace(/"/g,'&quot;')}"></script></body></html>`, [src]);
  // Debounced filename existence check
  useEffect(() => {
    if (!saveOpen) return;
    const base = (fileName || '').trim();
    if (!base) { setNameExists(null); return; }
    let alive = true;
    setCheckingName(true);
    const t = setTimeout(async () => {
      try {
        const res = await checkPrefabExists(base);
        if (alive) setNameExists(!!res.exists);
      } catch {
        if (alive) setNameExists(null);
      } finally {
        if (alive) setCheckingName(false);
      }
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [fileName, saveOpen]);

  const openSaveDialog = () => {
    setFileName('component-prefab');
    setDisplayName('Component Prefab');
    setSaveError(null);
    setNameExists(null);
    setSaveOpen(true);
  };

  const confirmSavePrefab = async () => {
    const base = (fileName || '').trim();
    if (!base) { setSaveError('Please enter a file name.'); return; }
    if (!/^[A-Za-z0-9._-]+$/.test(base)) { setSaveError('Only letters, numbers, dot, underscore and dash are allowed.'); return; }
    if (nameExists) { setSaveError('A prefab with this name already exists. Choose another name.'); return; }
    // Derive component path relative to uploads (e.g., components/cta-button.js)
    let rel = (componentUrl || '').replace(/^https?:\/\/[^/]+\//, '/');
    rel = rel.replace(/^\/+uploads\//, '');
    rel = rel.replace(/^\/+/, '');
    if (!rel) { setSaveError('Invalid component URL.'); return; }
    setSavingPrefab(true);
    setSaveError(null);
    try {
      await savePrefab({ fileName: base, displayName: (displayName || base), component: rel, props: { text, fontSize, textColor, bgColor, radius, padding } } as any);
      setSavingPrefab(false);
      setSaveOpen(false);
    } catch (e: any) {
      setSavingPrefab(false);
      setSaveError(e?.message || 'Failed to save prefab');
    }
  };

  return (
    <div className={open ? "fixed inset-0 z-[120] flex items-center justify-center bg-black/50" : "hidden"} onMouseDown={onClose}>
      <div className={(maximized ? "bg-white rounded-none w-screen h-screen" : "bg-white rounded-2xl w-full max-w-6xl h-[85vh]") + " shadow-2xl border border-gray-200 overflow-hidden"} onMouseDown={e=>e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-slate-50">
              <svg className="h-5 w-5 text-slate-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c0 .66.39 1.26 1 1.51H21a2 2 0 1 1 0 4h-.09c-.6.25-1 .85-1 1.49Z"/></svg>
            </div>
        {saveOpen && (
          <div className="absolute inset-0 z-[130] flex items-center justify-center bg-black/40 backdrop-blur-sm" onMouseDown={() => setSaveOpen(false)}>
            <div className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl border border-gray-700 bg-gray-800 text-gray-100" onMouseDown={e=>e.stopPropagation()}>
              <div className="px-5 py-4 border-b border-gray-700 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-gray-700/60">
                    <svg className="h-4 w-4 text-teal-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 3h10l4 4v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"/><path d="M7 3v8h10V3"/></svg>
                  </span>
                  <div className="font-semibold">Save Prefab</div>
                </div>
                <button className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-gray-700" onClick={() => setSaveOpen(false)}>
                  <svg className="h-4 w-4 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
                </button>
              </div>
              <div className="px-5 py-4 space-y-4">
                <label className="block text-sm">Prefab file name (without extension)
                  <input value={fileName} onChange={e=>{ setFileName(e.target.value); setSaveError(null); }} className="mt-1 w-full rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-teal-500" placeholder="my-prefab" />
                </label>
                <div className="text-xs text-gray-400">File will be saved to <span className="text-gray-200">components/prefabs/{(fileName||'').trim() || 'my-prefab'}.json</span></div>
                {checkingName ? (
                  <div className="text-xs text-gray-300">Checking name availability...</div>
                ) : nameExists === true ? (
                  <div className="text-xs text-red-400">A prefab with this name already exists.</div>
                ) : nameExists === false ? (
                  <div className="text-xs text-teal-400">Name is available.</div>
                ) : null}
                <label className="block text-sm">Display name (optional)
                  <input value={displayName} onChange={e=>setDisplayName(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-teal-500" placeholder="My Prefab" />
                </label>
                {saveError && <div className="text-sm text-red-400">{saveError}</div>}
              </div>
              <div className="px-5 pb-5 flex items-center justify-end gap-2">
                <button className="h-9 px-3 inline-flex items-center justify-center rounded-md border border-gray-600 bg-gray-800 hover:bg-gray-700 text-sm" onClick={() => setSaveOpen(false)}>Cancel</button>
                <button className="h-9 px-3 inline-flex items-center justify-center rounded-md bg-teal-500 hover:bg-teal-400 text-black text-sm disabled:opacity-60" disabled={checkingName || nameExists === true || savingPrefab} onClick={confirmSavePrefab}>
                  {savingPrefab ? 'Saving…' : 'Save Prefab'}
                </button>
              </div>
            </div>
          </div>
        )}
            <div className="flex items-center gap-3">
              <div className="font-semibold text-gray-900">{prefabMode ? 'Component (Prefab) Settings' : 'Component Settings'}</div>
              {prefabMode && (
                <span className="inline-flex items-center gap-1 rounded-md border border-purple-200 bg-purple-50 px-2 py-0.5 text-xs text-purple-700">
                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 8h10"/><path d="M7 12h6"/><path d="M7 16h3"/></svg>
                  Prefab
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="h-8 px-2 inline-flex items-center justify-center gap-1 rounded-md border border-gray-200 bg-white hover:bg-gray-50" onClick={openSaveDialog} aria-label="Save prefab">
              <svg className="h-4 w-4 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 3h10l4 4v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"/><path d="M7 3v8h10V3"/><path d="M7 21h10"/></svg>
              <span className="text-sm text-gray-800 font-medium">Save</span>
            </button>
            <button className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-gray-100" onClick={() => setMaximized(m => !m)} aria-label="Toggle size">
              {maximized ? (
                <svg className="h-4 w-4 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/></svg>
              ) : (
                <svg className="h-4 w-4 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3h7v2H5v5H3V3zM21 21h-7v-2h5v-5h2v7z"/></svg>
              )}
            </button>
            <button className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-gray-100" onClick={onClose} aria-label="Close">
              <svg className="h-4 w-4 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
            </button>
          </div>
        </div>
        <div className={(maximized ? "w-full h-[calc(100vh-64px)]" : "w-full h-[calc(85vh-64px)]") + " bg-white grid grid-cols-1 md:grid-cols-[320px_1fr]"}>
          <div className="border-r border-gray-200 p-4 space-y-4 bg-gray-50">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Appearance</div>
            <label className="block text-sm text-gray-700">Text
              <input value={text} onChange={e=>setText(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500" />
            </label>
            <label className="block text-sm text-gray-700">Font size (px)
              <input type="number" min={10} max={48} value={fontSize} onChange={e=>setFontSize(parseInt(e.target.value||'16',10))} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500" />
            </label>
            <label className="block text-sm text-gray-700">Text color
              <input type="color" value={textColor} onChange={e=>setTextColor(e.target.value)} className="mt-1 h-9 w-full rounded-lg border border-gray-300 p-1" />
            </label>
            <label className="block text-sm text-gray-700">Background color
              <input type="color" value={bgColor} onChange={e=>setBgColor(e.target.value)} className="mt-1 h-9 w-full rounded-lg border border-gray-300 p-1" />
            </label>
            <label className="block text-sm text-gray-700">Corner radius (px)
              <input type="number" min={0} max={32} value={radius} onChange={e=>setRadius(parseInt(e.target.value||'12',10))} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500" />
            </label>
            <label className="block text-sm text-gray-700">Padding (px)
              <input type="number" min={8} max={28} value={padding} onChange={e=>setPadding(parseInt(e.target.value||'12',10))} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500" />
            </label>
          </div>
          <div className="bg-white">
            <iframe ref={iframeRef} title="Component Settings Preview" className="w-full h-full bg-white" sandbox="allow-scripts allow-modals" srcDoc={doc} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComponentSettingsModal;
